import { FC, useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  U21Modal,
  U21Form,
  U21FormField,
  U21Spacer,
  U21Checkbox,
  U21Typography,
  U21InfoItem,
  U21Alert,
  U21SelectProps,
  useU21FormState,
} from 'app/shared/u21-ui/components';
import { required } from 'app/shared/utils/validation';
import {
  ActionMapping,
  ObjectType,
  ObjectActionType,
  TransactionData,
  InstrumentData,
} from 'app/modules/bankingIntegrations/models';
import { toSentenceCase } from 'app/shared/utils/string';
import { selectInstrumentReferenceDataSettings } from 'app/modules/dataSettings/selectors';
import { keyPathToLabel } from 'app/modules/dataSettings/utils';
import { useExecuteAction } from 'app/modules/bankingIntegrations/queries/useExecuteAction';

interface BaseActionModalProps {
  open: boolean;
  onClose: () => void;
  actionMapping?: ActionMapping;
}

type ActionModalProps = BaseActionModalProps & {
  objectType: ObjectType;
  actionData: TransactionData['actionData'] | InstrumentData['actionData'];
};

interface BankingActionModalFormValues {
  actionMappingId: number;
  instrumentExternalId?: string;
  transactionExternalId?: string;
}

const NEEDS_INSTRUMENT_EXTERNAL_ID: ReadonlyArray<ObjectActionType> = [
  ObjectActionType.BLOCK_CARD,
  ObjectActionType.ACTIVATE_CARD,
];

export const BankingActionModal: FC<ActionModalProps> = ({
  open,
  onClose,
  objectType,
  actionData,
  actionMapping,
}) => {
  const executeAction = useExecuteAction();
  const needsInstrumentExternalId: boolean =
    objectType === ObjectType.INSTRUMENT ||
    (objectType === ObjectType.TRANSACTION &&
      actionMapping?.action_type &&
      NEEDS_INSTRUMENT_EXTERNAL_ID.includes(actionMapping.action_type)) ||
    false;

  const instrumentReferenceDataSettings = useSelector(
    selectInstrumentReferenceDataSettings,
  );

  const instrumentOptions = useMemo<U21SelectProps<string>['options']>(() => {
    if (!needsInstrumentExternalId || objectType !== ObjectType.TRANSACTION)
      return [];
    const options: Array<U21SelectProps<string>['options'][number]> = [];
    const seenExternalIds = new Set<string>();
    if (!actionData || !actionData.formatted_data) return [];

    instrumentReferenceDataSettings.forEach((dataSetting) => {
      const formattedDataItem = actionData.formatted_data[dataSetting.id];
      if (formattedDataItem) {
        const formattedValue = formattedDataItem.formatted_value;
        const externalId =
          typeof formattedValue === 'object' && formattedValue !== null
            ? formattedValue.external_id
            : null;

        if (externalId && !seenExternalIds.has(externalId)) {
          seenExternalIds.add(externalId);
          const label = keyPathToLabel(dataSetting) || externalId;
          options.push({
            text: toSentenceCase(label),
            value: externalId,
            description: externalId,
          });
        }
      }
    });

    return options;
  }, [
    needsInstrumentExternalId,
    objectType,
    actionData,
    instrumentReferenceDataSettings,
  ]);

  const [confirmed, setConfirmed] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  useEffect(() => {
    if (!open) {
      setConfirmed(false);
      setIsFormValid(false);
    }
  }, [open]);

  const footerLeftAction = isFormValid ? (
    <U21Checkbox
      checked={confirmed}
      onChange={(checked) => setConfirmed(checked)}
      label="Confirm"
    />
  ) : undefined;

  if (!actionMapping) {
    return null;
  }

  return (
    <U21Modal
      open={open}
      onClose={onClose}
      title="Banking Action"
      footerLeftAction={footerLeftAction}
      actionButtonProps={{
        children: 'Submit',
        form: 'action-modal-form',
        type: 'submit',
        disabled: !confirmed,
        loading: executeAction.isPending,
      }}
    >
      <U21Form<BankingActionModalFormValues>
        id="action-modal-form"
        initialValues={{
          actionMappingId: actionMapping?.id ?? 0,
          transactionExternalId:
            objectType === ObjectType.TRANSACTION
              ? actionData.external_id
              : undefined,
          instrumentExternalId:
            objectType === ObjectType.INSTRUMENT
              ? actionData.external_id
              : undefined,
        }}
        onSubmit={(values) => {
          if (!actionMapping) return;

          const targetObjectExternalId = needsInstrumentExternalId
            ? values.instrumentExternalId
            : values.transactionExternalId;

          if (!targetObjectExternalId) return;

          executeAction.mutate(
            {
              mapping_id: actionMapping.id,
              target_object_external_id: targetObjectExternalId,
            },
            {
              onSuccess: () => {
                onClose();
              },
            },
          );
        }}
      >
        <FormContent
          objectType={objectType}
          actionMapping={actionMapping}
          needsInstrumentExternalId={needsInstrumentExternalId}
          instrumentOptions={instrumentOptions}
          isFormValid={isFormValid}
          setIsFormValid={setIsFormValid}
          setConfirmed={setConfirmed}
        />
      </U21Form>
    </U21Modal>
  );
};

interface FormContentProps {
  objectType: ObjectType;
  actionMapping: ActionMapping;
  needsInstrumentExternalId: boolean;
  instrumentOptions: U21SelectProps<string>['options'];
  isFormValid: boolean;
  setIsFormValid: (valid: boolean) => void;
  setConfirmed: (confirmed: boolean) => void;
}

const FormContent: FC<FormContentProps> = ({
  objectType,
  actionMapping,
  needsInstrumentExternalId,
  instrumentOptions,
  isFormValid,
  setIsFormValid,
  setConfirmed,
}) => {
  const { values, valid } = useU21FormState<BankingActionModalFormValues>({
    subscription: { values: true, valid: true },
  });

  useEffect(() => {
    const isValid: boolean = valid ?? false;
    if (isFormValid !== isValid) {
      setIsFormValid(isValid);
    }
  }, [valid, isFormValid, setIsFormValid]);

  return (
    <U21Spacer spacing={3}>
      {objectType === ObjectType.TRANSACTION &&
        actionMapping?.action_type &&
        NEEDS_INSTRUMENT_EXTERNAL_ID.includes(actionMapping.action_type) && (
          <U21FormField
            name="instrumentExternalId"
            label="Target instrument"
            type="SELECT"
            required
            validate={required('Target instrument is required')}
            fieldProps={{
              options: instrumentOptions,
              placeholder:
                instrumentOptions.length === 0
                  ? 'No instruments available'
                  : 'Select target instrument',
              disabled: instrumentOptions.length === 0,
              onChange: () => {
                setConfirmed(false);
              },
            }}
          />
        )}
      {valid && (
        <U21Alert color="normal">
          <U21Spacer spacing={3}>
            <U21Typography variant="body2">
              You are about to execute the following{' '}
              {actionMapping.action_type.replace(/_/g, ' ')} action:
            </U21Typography>

            <U21InfoItem label="Action">{actionMapping.name}</U21InfoItem>
            {needsInstrumentExternalId && values.instrumentExternalId && (
              <U21InfoItem label="Instrument ID">
                {values.instrumentExternalId}
              </U21InfoItem>
            )}
            {objectType === ObjectType.TRANSACTION &&
              values.transactionExternalId && (
                <U21InfoItem label="Transaction ID">
                  {values.transactionExternalId}
                </U21InfoItem>
              )}
          </U21Spacer>
        </U21Alert>
      )}
    </U21Spacer>
  );
};
